import { IconAlertCircle, IconSparkles } from '@tabler/icons-react';
import PropTypes from 'prop-types';

/**
 * Standing status line for the library's descriptions. Informational only — the
 * gallery has no description filter to send the tenant to, so there is no action
 * button; the undescribed tiles carry their own badge.
 *
 * Two states, and the order matters. While descriptions are being written the
 * tenant has nothing to do, so the generating state takes precedence — warning
 * them about images the system is already handling would be asking for work that
 * is about to be unnecessary. Once generation settles, whatever is still
 * undescribed gets the warning.
 *
 * The warning states the consequence on purpose: descriptions are the only signal
 * used to pick images for generated emails, so an undescribed image is dead weight.
 */
const NeedsDescriptionBanner = ({ count, describingCount }) => {
  if (describingCount > 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3">
        <IconSparkles size={20} className="shrink-0 animate-pulse text-primary-600" />
        <p className="flex-1 text-base text-primary-700">
          Writing {describingCount === 1 ? 'a description' : `${describingCount} descriptions`} with AI…{' '}
          <span className="text-primary-600">You can edit {describingCount === 1 ? 'it' : 'them'} afterwards.</span>
        </p>
      </div>
    );
  }

  if (!count) return null;

  return (
    <div className="flex items-center gap-3 rounded-lg border border-orange-100 bg-orange-50 px-4 py-3">
      <IconAlertCircle size={20} className="shrink-0 text-orange-700" />
      <p className="flex-1 text-base text-orange-700">
        <span className="font-semibold">
          {count} {count === 1 ? 'image has' : 'images have'} no description.
        </span>{' '}
        Descriptions are how we choose images for your generated emails — images without one are skipped.
      </p>
    </div>
  );
};

NeedsDescriptionBanner.propTypes = {
  count: PropTypes.number,
  describingCount: PropTypes.number,
};

NeedsDescriptionBanner.defaultProps = {
  count: 0,
  describingCount: 0,
};

export default NeedsDescriptionBanner;
